import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { PoliticalEntity, PoliticalEntityType, PoliticalEntityVisibility } from '@/types/models'
import { searchParams } from '@/services/core/listSearch'
import { mapValidationWarnings } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

const JSON_HEADERS = { 'Content-Type': 'application/json' }

interface ApiPoliticalEntityRef {
  id: string
  name: string
  objectState: string
  url: string
}

interface ApiPoliticalEntity {
  id: string
  name: string
  type: PoliticalEntityType
  objectState: string
  description?: string
  legalEntity?: string
  contactEmail?: string
  address: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    state?: string
    postCode?: string
    country?: string
  }
  siteUrl?: string
  vatId?: string
  network: ApiPoliticalEntityRef & { tagId: string }
  parent?: ApiPoliticalEntityRef
  visibilityType?: PoliticalEntityVisibility
  validationWarnings?: Array<{ code: string; text: string; preventsAdServing: boolean; parameters?: Record<string, string> }>
  ownerTeam: ApiPoliticalEntityRef & { type: PoliticalEntityType }
  createTime: string
  updateTime: string
}

export interface PoliticalEntityInput {
  name: string
  type: PoliticalEntityType
  description?: string
  legalEntity?: string
  contactEmail?: string
  address?: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    state?: string
    postCode?: string
    country?: string
  }
  siteUrl?: string
  vatId?: string
  ownerTeamId?: string
  parentId?: string
  objectState?: string
}

function toLocal(raw: ApiPoliticalEntity): PoliticalEntity {
  return {
    id: raw.id,
    name: raw.name,
    type: raw.type,
    objectState: raw.objectState,
    description: raw.description,
    legalEntity: raw.legalEntity,
    contactEmail: raw.contactEmail,
    address: raw.address ?? {},
    siteUrl: raw.siteUrl,
    vatId: raw.vatId,
    networkId: raw.network.id,
    networkName: raw.network.name,
    parentId: raw.parent?.id,
    parentName: raw.parent?.name,
    visibilityType: raw.visibilityType,
    validationWarnings: mapValidationWarnings(raw.validationWarnings),
    ownerTeamId: raw.ownerTeam.id,
    ownerTeamName: raw.ownerTeam.name,
    createTime: raw.createTime,
    updateTime: raw.updateTime,
  }
}

function toApiBody(input: PoliticalEntityInput) {
  const body: Record<string, unknown> = {
    name: input.name,
    type: input.type,
    description: input.description ?? '',
    legalEntity: input.legalEntity ?? '',
    contactEmail: input.contactEmail ?? '',
    address: input.address ?? {},
    siteUrl: input.siteUrl ?? '',
    vatId: input.vatId ?? '',
  }
  if (input.ownerTeamId) body.ownerTeam = input.ownerTeamId
  if (input.parentId) body.parent = input.parentId
  if (input.objectState) body.objectState = input.objectState
  return body
}

/** A sponsor or controller on the political network, as offered for link requests. */
export interface PoliticalSponsor {
  id: string
  /** What the link request names the organisation by. */
  publicId: string
  name: string
  contactEmail: string
}

interface ApiGroupEntity {
  id?: string
  publicId?: string
  name?: string
  type?: string
  contactEmail?: string
  visibilityType?: string
  validationWarnings?: unknown[]
}

const INVITE_LIST_PARAMS = {
  includeHidden: 'false',
  includeActive: 'true',
  includeInactive: 'false',
  pageSize: '2000',
}

// The backend only takes link requests for valid controllers and sponsors.
const LINKABLE_TYPES = ['SPONSOR', 'CONTROLLER', 'CONTROLLING']

function toSponsors(entities: ApiGroupEntity[]): PoliticalSponsor[] {
  const byId = new Map<string, PoliticalSponsor>()
  for (const e of entities) {
    const publicId = e?.publicId || e?.id
    if (!e?.id || !publicId || !LINKABLE_TYPES.includes(e.type ?? '')) continue
    if (e.validationWarnings?.length) continue
    byId.set(publicId, { id: e.id, publicId, name: e.name ?? '', contactEmail: e.contactEmail ?? '' })
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
}

export const PoliticalEntitiesApi = {
  /**
   * The sponsors an advertiser can be connected to.
   *
   * With system rights on the political network, its entities are listed
   * directly, TEST ones included. Anyone else gets the public transparency
   * api — anonymous, so it answers from any network (it still wants one as
   * context) — whose campaign groups only carry publicly visible sponsors that
   * have a valid parent controller.
   */
  getSponsors: async (politicalNetworkTagId?: string): Promise<PoliticalSponsor[]> => {
    if (politicalNetworkTagId) {
      const params = new URLSearchParams({
        ...INVITE_LIST_PARAMS,
        visibilityType: 'STANDARD;TEST',
        context: politicalNetworkTagId,
      })
      const res = await apiFetch(`${API_BASE}/politicalentities/byvisibility?${params}`, { headers: JSON_HEADERS })
      if (!res.ok) throw new Error(`Failed to fetch sponsors (${res.status})`)
      return toSponsors(toArray<ApiGroupEntity>(await res.json()))
    }
    const params = new URLSearchParams({ ...INVITE_LIST_PARAMS, context: getContext() })
    const res = await apiFetch(`${API_BASE}/public/politicalcampaigns/grouped?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch sponsors (${res.status})`)
    const groups = toArray<{ controller?: ApiGroupEntity; sponsor?: ApiGroupEntity }>(await res.json())
    return toSponsors(groups.flatMap((g) => [g.controller, g.sponsor].filter((e): e is ApiGroupEntity => !!e)))
  },

  getAll: async (search?: string): Promise<PoliticalEntity[]> => {
    const params = new URLSearchParams({
      ...searchParams('politicalEntities', search),
      context: getContext(),
    })
    const res = await apiFetch(`${API_BASE}/politicalentities?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch political entities (${res.status})`)
    const data = await res.json()
    const results = toArray<ApiPoliticalEntity>(data)
    return results.map(toLocal)
  },

  getById: async (id: string): Promise<PoliticalEntity | null> => {
    const res = await apiFetch(`${API_BASE}/politicalentities/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`Failed to fetch political entity (${res.status})`)
    return toLocal(await res.json())
  },

  create: async (id: string, input: PoliticalEntityInput): Promise<PoliticalEntity> => {
    const res = await apiFetch(`${API_BASE}/politicalentities/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...toApiBody(input) }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create political entity')
    return toLocal(await res.json())
  },

  update: async (id: string, input: PoliticalEntityInput): Promise<PoliticalEntity> => {
    const res = await apiFetch(`${API_BASE}/politicalentities/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...toApiBody(input) }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update political entity')
    return toLocal(await res.json())
  },
}
